/**
 * WorkshopDetailBody — the loaded detail page body (header + sections + rail +
 * publish modal). Extracted from WorkshopDetailPage so each component lives in
 * its own file (react/no-multi-comp) and the page component stays lean.
 */
import { useState } from 'react';

import type { WorkshopDetail, WorkshopPatch } from '../../../../api/workshops';
import { shellBasePath, workshopsListUrlFor, type WorkshopShell } from '../../workshopDetailUrlFor';
import { useDiscardState, usePublishWorkshop, useUpdateWorkshop } from '../useWorkshop';
import { isPublished } from '../workshop-lifecycle';

import { DetailGrid } from './DetailGrid';
import { DraftDiscarded } from './DraftDiscarded';
import { PublishConfirmModal } from './PublishConfirmModal';
import { useWorkshopEditForm } from './useWorkshopEditForm';
import { WorkshopDetailHeader } from './WorkshopDetailHeader';

export function WorkshopDetailBody({
  workshop,
  shell,
  readOnly,
}: {
  workshop: WorkshopDetail;
  shell: WorkshopShell;
  readOnly: boolean;
}) {
  const base = shellBasePath(shell);
  const venueHref = workshop.venueId ? `${base}/venues/${workshop.venueId}` : null;
  const workshopsHref = workshopsListUrlFor(shell, workshop.venueId);

  // A cancelled workshop is terminal: the record + edit sections go read-only
  // for everyone (BON-202). The rail still passes the caller's own `readOnly`
  // so the danger-zone banner shows for editors but never for Teacher.
  const cancelled = workshop.lifecycle === 'cancelled';
  const recordReadOnly = readOnly || cancelled;
  const showBookings = isPublished(workshop.lifecycle) || cancelled;

  const update = useUpdateWorkshop(workshop.id);
  const publish = usePublishWorkshop(workshop.id);
  const save = async (patch: WorkshopPatch) => {
    await update.mutateAsync(patch);
  };
  const form = useWorkshopEditForm(workshop, save);
  const discard = useDiscardState();

  // Admin = holds `write:all` (contract dec-3) — mirrored client-side so the
  // confirm modal can reflect the server's admin override of the 6-week lead.

  // BON-215: Publish now opens a pre-publish confirmation modal; Confirm invokes
  // the existing publish path. The modal closes on a successful publish; a 409
  // (readiness race) keeps it open and surfaces `publish.fieldErrors`.
  const [confirmingPublish, setConfirmingPublish] = useState(false);
  const openConfirm = () => {
    publish.reset();
    setConfirmingPublish(true);
  };
  const closeConfirm = () => {
    publish.reset();
    setConfirmingPublish(false);
  };
  const confirmPublish = async () => {
    // Close only when the publish actually landed. On failure the modal stays
    // open so the client blockers / server fieldErrors remain visible.
    const ok = await publish.run();
    if (ok) setConfirmingPublish(false);
  };

  if (discard.discarded) return <DraftDiscarded backHref={workshopsHref} />;

  const error = form.saveError ?? publish.errorMessage ?? discard.error;

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <WorkshopDetailHeader
        workshop={workshop}
        venueHref={venueHref}
        workshopsHref={workshopsHref}
        readOnly={recordReadOnly}
        editing={form.editing}
        publishing={publish.isPending}
        discarding={discard.discarding}
        onPublish={openConfirm}
        onDiscard={() => void discard.run(workshop.id)}
      />
      {error && !confirmingPublish && (
        <div style={{ fontSize: 11.5, color: '#c0392b' }} role="alert">
          {error}
        </div>
      )}
      <DetailGrid
        workshop={workshop}
        form={form}
        readOnly={readOnly}
        editable={!recordReadOnly}
        showBookings={showBookings}
      />
      {confirmingPublish && (
        <PublishConfirmModal
          workshop={workshop}
          publishing={publish.isPending}
          fieldErrors={publish.fieldErrors}
          onConfirm={() => void confirmPublish()}
          onClose={closeConfirm}
        />
      )}
    </div>
  );
}
